import React from "react";
import { useTranslation } from "react-i18next";
import { useProjects } from "../../common/contexts/ProjectContext";
import { useProjectNavigation } from "../../../hooks/useProjectNavigation";
import { RoleBrandingHeader } from "../../common/components/RoleBrandingHeader";
import { UserMenu } from "../../common/components/UserMenu";
import { BugReportButton } from "../../common/components/BugReportButton";
import { LanguageToggle } from "../../common/components/LanguageToggle";
import { FullscreenButton } from "../../common/components/FullscreenButton";
import { ButtonGroup } from "@/components/ui/button-group";
import { ConnectionStatusWidget } from "../../common/components/ConnectionStatusWidget";

const StoryProducer = React.lazy(() =>
  import("../../storyproducer/views/StoryProducer").then((m) => ({
    default: m.StoryProducer,
  })),
);

export const ClienteLayout: React.FC = () => {
  const { t } = useTranslation();
  const { activeProject, clearActiveProject } = useProjects();
  const { navigate } = useProjectNavigation();

  const ACCENT_COLOR = "var(--color-role-cliente)";

  if (!activeProject) return null;

  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        background: "var(--color-bg-dark, #000)",
        color: "var(--color-text-primary)",
        fontFamily: "var(--font-family)",
        overflow: "hidden",
      }}
      data-testid="cliente-layout"
    >
      <header
        style={{
          height: "88px",
          boxSizing: "border-box",
          padding: "0 min(40px, 3vw)",
          borderBottom: `5px solid ${ACCENT_COLOR}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "var(--color-panel-bg)",
          backdropFilter: "blur(10px)",
          boxShadow: `0 0 20px ${ACCENT_COLOR}15`,
          zIndex: 100,
          position: "relative",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
          <RoleBrandingHeader
            role="cliente"
            roleLabel={t("auth.roles.cliente", "CLIENTE").toUpperCase()}
            projectName={activeProject.name}
            onHomeClick={() => {
              clearActiveProject();
              navigate("/projects");
            }}
            testIdPrefix="cliente"
          />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            justifyContent: "flex-end",
            flex: 1,
          }}
        >
          <ButtonGroup>
            <LanguageToggle />
            <BugReportButton module="cliente" />
            <FullscreenButton isInline={true} />
          </ButtonGroup>
          <ConnectionStatusWidget />
          <UserMenu role="cliente" testIdPrefix="cliente-user" />
        </div>
      </header>

      <div
        data-testid="cliente-content"
        style={
          {
            flex: 1,
            display: "flex",
            overflow: "hidden",
            padding: "20px",
            gap: "20px",
            "--color-accent-primary": ACCENT_COLOR,
            "--color-accent-secondary": ACCENT_COLOR,
            "--color-btn-primary": ACCENT_COLOR,
            "--color-btn-hover": "rgba(6, 182, 212, 0.1)",
            "--color-highlight": "rgba(6, 182, 212, 0.2)",
            "--color-role-sp": ACCENT_COLOR,
            "--color-brand": ACCENT_COLOR,
            "--discovery-accent": ACCENT_COLOR,
          } as React.CSSProperties
        }
      >
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            background: "var(--color-panel-bg)",
            border: "1px solid var(--color-panel-border)",
            borderRadius: "var(--border-radius-lg)",
            overflow: "hidden",
          }}
        >
          <div style={{ flex: 1, overflow: "hidden" }}>
            <React.Suspense
              fallback={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    color: "rgba(255,255,255,0.3)",
                  }}
                >
                  Loading...
                </div>
              }
            >
              <StoryProducer isEmbedded={true} />
            </React.Suspense>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClienteLayout;
